import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import { pickBy } from '../../../util/common';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../../util/search';
import { resolveKeywordBarSubmit } from '../../../util/mainSearchQuery';
import { parse, stringify } from '../../../util/urlHelpers';
import { createResourceLocatorString, matchPathname, pathByRouteName } from '../../../util/routes';
import {
  Button,
  CartIcon,
  IconArrowHead,
  LimitedAccessBanner,
  LinkedLogo,
  Modal,
  ModalMissingInformation,
} from '../../../components';
// Deliberately not in the components barrel: this is mounted exactly once, from here.
import CartLifecycle from '../../../components/CartIcon/CartLifecycle';
import { getSearchPageResourceLocatorStringParams } from '../../SearchPage/SearchPage.shared';

import MenuIcon from './MenuIcon';
import SearchIcon from './SearchIcon';
import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import MobileSearchQuickTags from './MobileSearchQuickTags/MobileSearchQuickTags';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';
import MobileBottomNav from './MobileBottomNav/MobileBottomNav';

import css from './Topbar.module.css';
import { getCurrentUserTypeRoles, showCreateListingLinkForUser } from '../../../util/userHelpers';

// Align with --viewportMedium (min-width: 768px): mobile chrome below 768px.
const MAX_MOBILE_SCREEN_WIDTH = 767;

// Match SearchFiltersMobile / ModalInMobile bottomSheetSlideUp (0.28s ease-out).
const DRAWER_ANIM_MS = 280;
const PAGE_ROOT_ID = 'root';

const SEARCH_DISPLAY_ALWAYS = 'always';
const SEARCH_DISPLAY_NOT_LANDING_PAGE = 'notLandingPage';
const SEARCH_DISPLAY_ONLY_SEARCH_PAGE = 'onlySearchPage';
const MOBILE_MENU_BUTTON_ID = 'mobileMenuButton';
const MOBILE_SEARCH_BUTTON_ID = 'mobileSearchButton';
const MOBILE_MENU_MODAL_ID = 'TopbarMobileMenu';
// Coalesce window focus + visibilitychange (both fire when returning to a tab).
const FORUM_UNREAD_REFRESH_MIN_MS = 2000;

const redirectToURLWithModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
  // replace: modal chrome is UI-only — avoid history entries that re-trigger route loadData
  history.replace(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.replace(`${pathname}${searchString}`, state);
};

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  // Note: sort order is stable in JS
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};
// Returns links in order where primary links are returned first
const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? [...customLinks] : [];
  return links.sort(compareGroups);
};

// Resolves in-app links against route configuration
const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.map(linkConfig => {
    const { type, href } = linkConfig;
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      // Internal link
      try {
        const testURL = new URL('http://my.marketplace.com' + href);
        const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
        if (matchedRoutes.length > 0) {
          const found = matchedRoutes[0];
          const to = { search: testURL.search, hash: testURL.hash };
          return {
            ...linkConfig,
            route: {
              name: found.route?.name,
              params: found.params,
              to,
            },
          };
        }
      } catch (e) {
        return linkConfig;
      }
    }
    return linkConfig;
  });
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'TransactionsPage'
    ? `TransactionsPage:${found.params?.tab}`
    : found.route?.name === 'InboxPage'
    ? `InboxPage:${found.params?.tab}`
    : null;
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName ? cmsPageName : inboxPageName ? inboxPageName : `${found.route?.name}`;
  }
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return show ? (
    <div className={classes} role="alert">
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  ) : null;
};

const TopbarComponent = props => {
  const drawerContainerRef = useRef(null);
  const isClosingDrawerRef = useRef(false);
  const closeAnimTimeoutRef = useRef(null);
  const [isMobileLayout, setIsMobileLayout] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return true;
    }
    return window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`).matches;
  });
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);

  const {
    className,
    rootClassName,
    desktopClassName,
    mobileRootClassName,
    mobileClassName,
    isAuthenticated,
    isLoggedInAs,
    authScopes = [],
    authInProgress,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentPage,
    notificationCount = 0,
    forumUnreadCount = 0,
    onFetchForumUnread,
    intl,
    history,
    location,
    onManageDisableScrolling,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    showGenericError,
    config,
    routeConfiguration,
  } = props;

  const handleSubmit = values => {
    const { currentSearchParams, history, location, config, routeConfiguration } = props;

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        return resolveKeywordBarSubmit(currentSearchParams, values);
      }
      // topbar search defaults to 'location' search
      const { search, selectedPlace } = values?.location || {};
      const { origin, bounds } = selectedPlace || {};
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
      };
    };
    const searchParams = isMainSearchTypeKeywords(config)
      ? topbarSearchParams()
      : {
          ...currentSearchParams,
          ...topbarSearchParams(),
        };

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, searchParams)
    );
  };

  const handleLogout = () => {
    const { onLogout, history, routeConfiguration } = props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      console.log('logged out'); // eslint-disable-line
    });
  };

  const showCreateListingsLink = showCreateListingLinkForUser(config, currentUser);
  const { customer: isCustomer, provider: isProvider } = getCurrentUserTypeRoles(
    config,
    currentUser
  );

  /**
   * Determine which tab to use in the transactions link:
   * - if only provider role – sales
   * - if only customer role – purchases
   * - if both roles – determine by currentUserHasListings value
   */
  const topbarInboxTab = !isCustomer
    ? 'sales'
    : !isProvider
    ? 'purchases'
    : currentUserHasListings
    ? 'sales'
    : 'purchases';

  const { mobilemenu, mobilesearch, keywords, address, origin, bounds } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  // Custom links are sorted so that group="primary" are always at the beginning of the list.
  const sortedCustomLinks = sortCustomLinks(config.topbar?.customLinks);
  const customLinks = getResolvedCustomLinks(sortedCustomLinks, routeConfiguration);
  const resolvedCurrentPage = currentPage || getResolvedCurrentPage(location, routeConfiguration);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }
    const mediaQuery = window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`);
    const syncMobileLayout = event => {
      setIsMobileLayout(event.matches);
    };
    setIsMobileLayout(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', syncMobileLayout);
      return () => mediaQuery.removeEventListener('change', syncMobileLayout);
    }
    // Safari < 14
    mediaQuery.addListener(syncMobileLayout);
    return () => mediaQuery.removeListener(syncMobileLayout);
  }, []);

  const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';
  const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';

  useEffect(() => {
    if (!isMobileSearchOpen) {
      setSuggestionsOpen(false);
    }
  }, [isMobileSearchOpen]);

  const clearDrawerInlineStyles = useCallback(() => {
    const drawer = drawerContainerRef.current;
    if (drawer) {
      drawer.style.animation = '';
      drawer.style.transition = '';
      drawer.style.transform = '';
      const scrim = drawer.parentElement;
      if (scrim) {
        scrim.style.transition = '';
        scrim.style.opacity = '';
        scrim.style.animation = '';
      }
    }
  }, []);

  const clearCloseAnimation = useCallback(() => {
    if (closeAnimTimeoutRef.current) {
      clearTimeout(closeAnimTimeoutRef.current);
      closeAnimTimeoutRef.current = null;
    }
    isClosingDrawerRef.current = false;
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      clearCloseAnimation();
      clearDrawerInlineStyles();
    }
  }, [isMobileMenuOpen, clearCloseAnimation, clearDrawerInlineStyles]);

  useEffect(() => {
    return () => {
      clearCloseAnimation();
    };
  }, [clearCloseAnimation]);

  const requestCloseMobileMenu = useCallback(() => {
    if (isClosingDrawerRef.current || !isMobileMenuOpen) {
      return;
    }

    const drawer = drawerContainerRef.current;
    if (!drawer) {
      redirectToURLWithoutModalState(history, location, 'mobilemenu');
      return;
    }

    isClosingDrawerRef.current = true;

    // Modal.handleClose unlocks body scroll before onClose runs. Re-lock for the
    // slide-out window so the page cannot move under the still-visible drawer.
    onManageDisableScrolling(MOBILE_MENU_MODAL_ID, true);
    document.getElementById(PAGE_ROOT_ID)?.setAttribute('inert', '');

    const scrim = drawer.parentElement;
    drawer.style.animation = 'none';
    drawer.style.transition = 'none';
    drawer.style.transform = 'translateX(0)';
    if (scrim) {
      scrim.style.animation = 'none';
      scrim.style.transition = 'none';
      scrim.style.opacity = '1';
    }
    // Force reflow so the browser applies the starting transform before transitioning.
    void drawer.offsetHeight;

    const finishClose = () => {
      if (!isClosingDrawerRef.current) {
        return;
      }
      drawer.removeEventListener('transitionend', onTransitionEnd);
      clearCloseAnimation();
      clearDrawerInlineStyles();
      document.getElementById(PAGE_ROOT_ID)?.removeAttribute('inert');
      redirectToURLWithoutModalState(history, location, 'mobilemenu');
    };

    const onTransitionEnd = event => {
      if (event.target !== drawer || event.propertyName !== 'transform') {
        return;
      }
      finishClose();
    };

    drawer.addEventListener('transitionend', onTransitionEnd);
    const duration = `${DRAWER_ANIM_MS / 1000}s`;
    drawer.style.transition = `transform ${duration} ease-out`;
    drawer.style.transform = 'translateX(-102%)';
    if (scrim) {
      scrim.style.transition = `opacity ${duration} ease-out`;
      scrim.style.opacity = '0';
    }

    closeAnimTimeoutRef.current = setTimeout(finishClose, DRAWER_ANIM_MS + 50);
  }, [
    clearCloseAnimation,
    clearDrawerInlineStyles,
    history,
    isMobileMenuOpen,
    location,
    onManageDisableScrolling,
  ]);

  const notificationDot =
    notificationCount > 0 ? <div className={css.notificationDot} aria-hidden="true" /> : null;

  useEffect(() => {
    if (!isAuthenticated || typeof onFetchForumUnread !== 'function') {
      return undefined;
    }
    if (typeof window === 'undefined') {
      return undefined;
    }

    let lastFetchedAt = 0;
    const fetchUnread = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }
      const now = Date.now();
      if (now - lastFetchedAt < FORUM_UNREAD_REFRESH_MIN_MS) {
        return;
      }
      lastFetchedAt = now;
      Promise.resolve(onFetchForumUnread()).catch(() => {});
    };

    fetchUnread();

    window.addEventListener('focus', fetchUnread);
    document.addEventListener('visibilitychange', fetchUnread);
    return () => {
      window.removeEventListener('focus', fetchUnread);
      document.removeEventListener('visibilitychange', fetchUnread);
    };
  }, [isAuthenticated, onFetchForumUnread]);

  const mobileMenu = (
    <div className={css.menuDrawerInner}>
      {authInProgress ? null : (
        <TopbarMobileMenu
          isAuthenticated={isAuthenticated}
          currentUser={currentUser}
          onLogout={handleLogout}
          currentPage={resolvedCurrentPage}
          inboxTab={topbarInboxTab}
          notificationCount={notificationCount}
          forumUnreadCount={forumUnreadCount}
          customLinks={customLinks}
          showCreateListingsLink={showCreateListingsLink}
        />
      )}
    </div>
  );

  const topbarSearcInitialValues = () => {
    if (isMainSearchTypeKeywords(config)) {
      return { keywords };
    }

    // Only render current search if full place object is available in the URL params
    const locationFieldsPresent = isOriginInUse(config)
      ? address && origin && bounds
      : address && bounds;
    return {
      location: locationFieldsPresent
        ? {
            search: address,
            selectedPlace: { address, origin, bounds },
          }
        : null,
    };
  };
  const initialSearchFormValues = topbarSearcInitialValues();

  const classes = classNames(rootClassName || css.root, className);

  const { display: searchFormDisplay = SEARCH_DISPLAY_ALWAYS } = config?.topbar?.searchBar || {};

  // Search form is shown conditionally depending on configuration and
  // the current page.
  const showSearchOnAllPages = searchFormDisplay === SEARCH_DISPLAY_ALWAYS;
  const showSearchOnSearchPage =
    searchFormDisplay === SEARCH_DISPLAY_ONLY_SEARCH_PAGE &&
    ['SearchPage', 'SearchPageWithListingType'].includes(resolvedCurrentPage);
  const showSearchNotOnLandingPage =
    searchFormDisplay === SEARCH_DISPLAY_NOT_LANDING_PAGE && resolvedCurrentPage !== 'LandingPage';

  const showSearchForm =
    resolvedCurrentPage !== 'LandingPage' &&
    (showSearchOnAllPages || showSearchOnSearchPage || showSearchNotOnLandingPage);

  const mobileSearchButtonMaybe = showSearchForm ? (
    <Button
      id={MOBILE_SEARCH_BUTTON_ID}
      rootClassName={css.searchMenu}
      onClick={() => redirectToURLWithModalState(history, location, 'mobilesearch')}
      title={intl.formatMessage({ id: 'Topbar.searchIcon' })}
    >
      <SearchIcon
        className={css.searchMenuIcon}
        ariaLabel={intl.formatMessage({ id: 'Topbar.searchIcon' })}
      />
    </Button>
  ) : (
    <div className={css.searchMenu} />
  );

  const handleSkipToMainContent = e => {
    e.preventDefault();
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Focus the main content for screen readers
      mainContent.setAttribute('tabindex', '-1');
      mainContent.focus();
      // Remove tabindex after blur to avoid tabbing into it later
      mainContent.addEventListener(
        'blur',
        () => {
          mainContent.removeAttribute('tabindex');
        },
        { once: true }
      );
    }
  };

  return (
    <div className={classes}>
      <Button onClick={handleSkipToMainContent} className={css.skipToMainContent}>
        <FormattedMessage id="Topbar.skipToMainContent" />
        <IconArrowHead direction="right" size="small" rootClassName={css.skiptoMainArrow} />
      </Button>
      <LimitedAccessBanner
        isAuthenticated={isAuthenticated}
        isLoggedInAs={isLoggedInAs}
        authScopes={authScopes}
        currentUser={currentUser}
        onLogout={handleLogout}
        currentPage={resolvedCurrentPage}
      />
      <nav className={classNames(mobileRootClassName || css.container, mobileClassName)}>
        <Button
          id={MOBILE_MENU_BUTTON_ID}
          rootClassName={css.menu}
          onClick={() => redirectToURLWithModalState(history, location, 'mobilemenu')}
          title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
        >
          <MenuIcon
            className={css.menuIcon}
            ariaLabel={intl.formatMessage({ id: 'Topbar.menuIcon' })}
          />
          {notificationDot}
        </Button>
        <LinkedLogo
          id="logo-topbar-mobile"
          className={css.logoLink}
          layout={'mobile'}
          alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
          linkToExternalSite={config?.topbar?.logoLink}
          showBrandName
        />
        {mobileSearchButtonMaybe}
        {/* Signed in or out: a guest cart is a real cart. */}
        <CartIcon
          className={css.cartLink}
          iconWrapClassName={css.cartIconWrap}
          badgeClassName={css.cartBadge}
        />
      </nav>
      <div className={css.desktop}>
        <TopbarDesktop
          className={desktopClassName}
          currentUserHasListings={currentUserHasListings}
          currentUser={currentUser}
          currentPage={resolvedCurrentPage}
          initialSearchFormValues={initialSearchFormValues}
          intl={intl}
          isAuthenticated={isAuthenticated}
          notificationCount={notificationCount}
          forumUnreadCount={forumUnreadCount}
          onLogout={handleLogout}
          onSearchSubmit={handleSubmit}
          config={config}
          customLinks={customLinks}
          showSearchForm={showSearchForm}
          showCreateListingsLink={showCreateListingsLink}
          inboxTab={topbarInboxTab}
        />
      </div>
      <Modal
        id={MOBILE_MENU_MODAL_ID}
        className={isMobileMenuOpen ? css.menuDrawerModal : null}
        scrollLayerClassName={css.menuDrawerScrollLayer}
        containerClassName={css.menuDrawerContainer}
        containerRef={drawerContainerRef}
        isOpen={isMobileMenuOpen}
        onClose={requestCloseMobileMenu}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        focusElementId={MOBILE_MENU_BUTTON_ID}
        lightCloseButton
        closeOnOverlayClick
      >
        {mobileMenu}
      </Modal>
      <Modal
        id="TopbarMobileSearch"
        containerClassName={css.modalContainerSearchForm}
        isOpen={isMobileSearchOpen}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilesearch')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        focusElementId={MOBILE_SEARCH_BUTTON_ID}
      >
        <div className={css.searchContainer}>
          <h2 className={css.mobileSearchTitle}>
            <FormattedMessage id="Topbar.mobileSearchTitle" />
          </h2>
          <TopbarSearchForm
            onSubmit={handleSubmit}
            initialValues={initialSearchFormValues}
            isMobile
            appConfig={config}
            onSuggestionsOpenChange={setSuggestionsOpen}
          />
          {suggestionsOpen ? null : <MobileSearchQuickTags />}
        </div>
      </Modal>
      <ModalMissingInformation
        id="MissingInformationReminder"
        containerClassName={css.missingInformationModal}
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        location={location}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
      />

      {/* Always mount: CSS hides ≥768px; avoids matchMedia/SSR mount gaps */}
      <MobileBottomNav
        isAuthenticated={isAuthenticated}
        currentPage={resolvedCurrentPage}
        notificationCount={notificationCount}
        forumUnreadCount={forumUnreadCount}
        inboxTab={topbarInboxTab}
      />

      {/* Renders nothing; owns cart hydration and the login merge. Mount once only. */}
      <CartLifecycle />

      <GenericError show={showGenericError} />
    </div>
  );
};

/**
 * Topbar containing logo, main search and navigation links.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.desktopClassName add more style rules for TopbarDesktop
 * @param {Object} props.mobileRootClassName overwrite mobile layout root classes
 * @param {Object} props.mobileClassName add more style rules for mobile layout
 * @param {boolean} props.isAuthenticated
 * @param {boolean} props.isLoggedInAs
 * @param {Object} props.currentUser
 * @param {boolean} props.currentUserHasListings
 * @param {boolean} props.currentUserHasOrders
 * @param {string} props.currentPage
 * @param {number} props.notificationCount
 * @param {number} [props.forumUnreadCount] unread XenForo DM count
 * @param {Function} [props.onFetchForumUnread] client-only unread refetch
 * @param {Function} props.onLogout
 * @param {Function} props.onManageDisableScrolling
 * @param {Function} props.onResendVerificationEmail
 * @param {Object} props.sendVerificationEmailInProgress
 * @param {Object} props.sendVerificationEmailError
 * @param {boolean} props.showGenericError
 * @param {Object} props.history
 * @param {Function} props.history.push
 * @param {Object} props.location
 * @param {string} props.location.search '?foo=bar'
 * @returns {JSX.Element} topbar component
 */
const Topbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...props}
    />
  );
};

export default Topbar;
